import React, { useState, useEffect, useRef, useMemo } from 'react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import './NoticePopup.css';
import './PopupAdmin.css';

const empty = {
  title: '',
  title_en: '',
  content: '',
  content_en: '',
  image_url: '',
  image_only: false,
  show_logo: true,
  link_url: '',
  link_notice_idx: '',
  start_date: '',
  end_date: '',
  priority: 0,
  is_active: true,
  width: 400,
  position: 'top-left',
};

function PopupAdmin({ token, onUnauthorized }) {
  const [popups, setPopups] = useState([]);
  const [noticeList, setNoticeList] = useState([]);
  const [editMode, setEditMode] = useState(null); // null | 'new' | idx
  const [form, setForm] = useState(empty);
  const [previewing, setPreviewing] = useState(false);

  const headers = {
    'Content-Type': 'application/json',
    'Authorization': 'Bearer ' + token,
  };

  const load = () => {
    fetch('/api/admin/popups', { headers })
      .then(res => {
        if (res.status === 401) { onUnauthorized && onUnauthorized(); return []; }
        return res.json();
      })
      .then(data => { if (Array.isArray(data)) setPopups(data); })
      .catch(() => {});
  };

  useEffect(() => {
    if (token) {
      load();
      // 공지사항 목록도 로드 (드롭다운용)
      fetch('/api/admin/notices', { headers })
        .then(res => res.json())
        .then(data => { if (Array.isArray(data)) setNoticeList(data); })
        .catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const handleSave = (e) => {
    e.preventDefault();
    const isNew = editMode === 'new';
    const url = isNew ? '/api/admin/popup' : `/api/admin/popup/${editMode}`;
    const method = isNew ? 'POST' : 'PUT';

    // 빈 문자열은 null로 (DATETIME 컬럼 빈 문자열 거부 대비)
    const payload = {
      ...form,
      start_date: form.start_date || null,
      end_date: form.end_date || null,
      image_url: form.image_url || null,
      link_url: form.link_url || null,
      link_notice_idx: form.link_notice_idx ? parseInt(form.link_notice_idx, 10) : null,
      width: parseInt(form.width, 10) || 400,
      priority: parseInt(form.priority, 10) || 0,
      position: form.position || 'top-left',
      show_logo: !!form.show_logo,
    };

    fetch(url, { method, headers, body: JSON.stringify(payload) })
      .then(res => res.json())
      .then(data => {
        if (data.success) {
          setEditMode(null);
          setForm(empty);
          load();
        } else {
          alert(data.error || '저장 실패');
        }
      })
      .catch(() => alert('서버 오류'));
  };

  const handleEdit = (p) => {
    setEditMode(p.IDX);
    setForm({
      title: p.TITLE || '',
      title_en: p.TITLE_EN || '',
      content: p.CONTENT || '',
      content_en: p.CONTENT_EN || '',
      image_url: p.IMAGE_URL || '',
      image_only: p.IMAGE_ONLY === 1,
      show_logo: p.SHOW_LOGO !== 0,
      link_url: p.LINK_URL || '',
      link_notice_idx: p.LINK_NOTICE_IDX || '',
      start_date: p.START_DATE ? toDateTimeLocal(p.START_DATE) : '',
      end_date: p.END_DATE ? toDateTimeLocal(p.END_DATE) : '',
      priority: p.PRIORITY || 0,
      is_active: p.IS_ACTIVE === 1,
      width: p.WIDTH || 400,
      position: p.POSITION || 'top-left',
    });
  };

  const handleDelete = (idx) => {
    if (!window.confirm('정말 삭제하시겠습니까?')) return;
    fetch(`/api/admin/popup/${idx}`, { method: 'DELETE', headers })
      .then(res => res.json())
      .then(data => { if (data.success) load(); });
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('image', file);
    fetch('/api/admin/upload', {
      method: 'POST',
      headers: { 'Authorization': 'Bearer ' + token },
      body: formData,
    })
      .then(res => res.json())
      .then(data => {
        if (data.success) setForm(prev => ({ ...prev, image_url: data.url }));
        else alert(data.error || '업로드 실패');
      });
  };

  const formatDate = (s) => {
    if (!s) return '-';
    const d = new Date(s);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  };

  // 등록/수정 화면
  if (editMode) {
    return (
      <form className="popup-form" onSubmit={handleSave}>

        {/* 카드 1 — 기본 정보 */}
        <div className="popup-card">
          <div className="popup-card-title"><span className="ico">📝</span> 기본 정보</div>

          <div className="popup-toggle-card" style={{marginBottom:16}}>
            <input
              type="checkbox"
              id="popup-active"
              checked={form.is_active}
              onChange={e => setForm({ ...form, is_active: e.target.checked })}
            />
            <div>
              <label htmlFor="popup-active">활성</label>
              <div className="desc">체크 해제 시 표시 안 됨</div>
            </div>
          </div>

          <div className="popup-field">
            <label>제목 <span className="req">*</span> <span className="hint">(한국어)</span></label>
            <input
              type="text"
              value={form.title}
              onChange={e => setForm({ ...form, title: e.target.value })}
              placeholder="팝업 상단에 표시될 제목"
              required
            />
          </div>

          <div className="popup-field">
            <label>Title <span className="hint">(English — optional, 비우면 한국어로 표시)</span></label>
            <input
              type="text"
              value={form.title_en}
              onChange={e => setForm({ ...form, title_en: e.target.value })}
              placeholder="English title"
            />
          </div>

          <div className="popup-field">
            <label>내용 <span className="hint">(한국어 · 텍스트 서식·이미지 삽입 가능)</span></label>
            <div className="popup-quill-wrap">
              <ContentEditor
                value={form.content}
                onChange={val => setForm(prev => ({ ...prev, content: val }))}
                token={token}
              />
            </div>
          </div>

          <div className="popup-field">
            <label>Content <span className="hint">(English — optional)</span></label>
            <div className="popup-quill-wrap">
              <ContentEditor
                value={form.content_en}
                onChange={val => setForm(prev => ({ ...prev, content_en: val }))}
                token={token}
              />
            </div>
          </div>
        </div>

        {/* 카드 2 — 이미지 */}
        <div className="popup-card">
          <div className="popup-card-title"><span className="ico">🖼️</span> 대표 이미지</div>

          <div className="popup-field">
            <label className="popup-image-upload-btn">
              📁 이미지 업로드
              <input type="file" accept="image/*" onChange={handleImageUpload} />
            </label>

            {form.image_url && (
              <div className="popup-image-preview">
                <img src={form.image_url} alt="" />
                <button
                  type="button"
                  className="remove-btn"
                  onClick={() => setForm({ ...form, image_url: '', image_only: false })}
                >이미지 제거</button>
              </div>
            )}
          </div>

          {form.image_url && (
            <div className="popup-toggle-card">
              <input
                type="checkbox"
                id="popup-img-only"
                checked={form.image_only}
                onChange={e => setForm({ ...form, image_only: e.target.checked })}
              />
              <div>
                <label htmlFor="popup-img-only">이미지 전용 모드</label>
                <div className="desc">제목·내용 숨기고 이미지만 크게 표시</div>
              </div>
            </div>
          )}
        </div>

        {/* 카드 3 — 클릭 시 이동 */}
        <div className="popup-card">
          <div className="popup-card-title"><span className="ico">🔗</span> 클릭 시 이동 (선택)</div>

          <div className="popup-field">
            <label>공지사항 선택</label>
            <select
              value={form.link_notice_idx}
              onChange={e => setForm({ ...form, link_notice_idx: e.target.value, link_url: e.target.value ? '' : form.link_url })}
            >
              <option value="">선택 안 함</option>
              {noticeList.map(n => (
                <option key={n.IDX} value={n.IDX}>
                  #{n.IDX} — {n.TITLE}
                </option>
              ))}
            </select>
            <div className="small-help">공지를 선택하면 팝업 클릭 시 해당 공지 상세로 이동</div>
          </div>

          <div className="popup-field">
            <label>외부 URL <span className="hint">공지 선택 안 했을 때만 사용</span></label>
            <input
              type="text"
              value={form.link_url}
              onChange={e => setForm({ ...form, link_url: e.target.value })}
              placeholder="https://example.com"
              disabled={!!form.link_notice_idx}
            />
          </div>
        </div>

        {/* 카드 4 — 표시 기간 */}
        <div className="popup-card">
          <div className="popup-card-title"><span className="ico">📅</span> 표시 기간</div>

          <div className="popup-row">
            <div className="popup-field">
              <label>시작 <span className="req">*</span></label>
              <input
                type="datetime-local"
                value={form.start_date}
                onChange={e => setForm({ ...form, start_date: e.target.value })}
                required
              />
            </div>
            <div className="popup-field">
              <label>종료 <span className="req">*</span></label>
              <input
                type="datetime-local"
                value={form.end_date}
                onChange={e => setForm({ ...form, end_date: e.target.value })}
                required
              />
            </div>
          </div>
          <div className="small-help" style={{marginTop:8}}>⚠ 미입력 시 팝업이 표시되지 않습니다</div>
        </div>

        {/* 카드 5 — 표시 옵션 */}
        <div className="popup-card">
          <div className="popup-card-title"><span className="ico">⚙️</span> 표시 옵션</div>

          <div className="popup-toggle-card" style={{marginBottom:14}}>
            <input
              type="checkbox"
              id="popup-show-logo"
              checked={form.show_logo}
              onChange={e => setForm({ ...form, show_logo: e.target.checked })}
            />
            <div>
              <label htmlFor="popup-show-logo">메디로지스 로고 표시</label>
              <div className="desc">팝업 상단에 ML 로고 자동 삽입</div>
            </div>
          </div>

          <div className="popup-field">
            <label>위치</label>
            <select
              value={form.position}
              onChange={e => setForm({ ...form, position: e.target.value })}
            >
              <option value="top-left">좌상단 (전통적)</option>
              <option value="top-center">상단 가운데</option>
              <option value="top-right">우상단</option>
              <option value="center">정중앙</option>
              <option value="bottom-left">좌하단</option>
              <option value="bottom-right">우하단</option>
            </select>
          </div>

          <div className="popup-row">
            <div className="popup-field">
              <label>우선순위 <span className="hint">큰 값이 먼저 표시</span></label>
              <input
                type="number"
                value={form.priority}
                onChange={e => setForm({ ...form, priority: e.target.value })}
              />
            </div>
            <div className="popup-field">
              <label>너비 (px) <span className="hint">기본 400</span></label>
              <input
                type="number"
                value={form.width}
                onChange={e => setForm({ ...form, width: e.target.value })}
              />
            </div>
          </div>
        </div>

        <div className="popup-form-actions">
          <button type="button" className="popup-btn popup-btn-cancel" onClick={() => { setEditMode(null); setForm(empty); }}>취소</button>
          <button type="button" className="popup-btn popup-btn-preview" onClick={() => setPreviewing(true)}>미리보기</button>
          <button type="submit" className="popup-btn popup-btn-primary">{editMode === 'new' ? '등록' : '수정 완료'}</button>
        </div>

        {previewing && <PreviewOverlay form={form} onClose={() => setPreviewing(false)} />}
      </form>
    );
  }

  // 목록 화면
  return (
    <>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'flex-end' }}>
        <button
          className="admin-btn-primary"
          onClick={() => { setForm(empty); setEditMode('new'); }}
        >팝업 등록</button>
      </div>
      <table className="admin-table">
        <thead>
          <tr>
            <th>번호</th>
            <th>제목</th>
            <th>활성</th>
            <th>우선순위</th>
            <th>시작</th>
            <th>종료</th>
            <th>관리</th>
          </tr>
        </thead>
        <tbody>
          {popups.length === 0 ? (
            <tr><td colSpan="7" style={{ textAlign: 'center', padding: 40, color: '#888' }}>등록된 팝업이 없습니다.</td></tr>
          ) : popups.map(p => (
            <tr key={p.IDX}>
              <td>{p.IDX}</td>
              <td>{p.TITLE}</td>
              <td>
                {p.IS_ACTIVE === 1
                  ? <span style={{ color: '#0066FF', fontWeight: 600 }}>ON</span>
                  : <span style={{ color: '#999' }}>OFF</span>}
              </td>
              <td>{p.PRIORITY || 0}</td>
              <td>{formatDate(p.START_DATE)}</td>
              <td>{formatDate(p.END_DATE)}</td>
              <td>
                <button className="admin-btn-sm" onClick={() => handleEdit(p)}>수정</button>
                <button className="admin-btn-sm admin-btn-del" onClick={() => handleDelete(p.IDX)}>삭제</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}

function ContentEditor({ value, onChange, token }) {
  const quillRef = useRef(null);

  const imageHandler = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.click();
    input.onchange = () => {
      const file = input.files[0];
      if (!file) return;
      const fd = new FormData();
      fd.append('image', file);
      fetch('/api/admin/upload', {
        method: 'POST',
        headers: { 'Authorization': 'Bearer ' + token },
        body: fd,
      })
        .then(r => r.json())
        .then(data => {
          if (data.success && quillRef.current) {
            const editor = quillRef.current.getEditor();
            const range = editor.getSelection(true);
            editor.insertEmbed(range.index, 'image', data.url);
            editor.setSelection(range.index + 1);
          } else {
            alert(data.error || '업로드 실패');
          }
        });
    };
  };

  const modules = useMemo(() => ({
    toolbar: {
      container: [
        [{ 'header': [1, 2, 3, false] }],
        ['bold', 'italic', 'underline'],
        [{ 'color': [] }, { 'background': [] }],
        [{ 'list': 'ordered' }, { 'list': 'bullet' }],
        [{ 'align': [] }],
        ['link', 'image'],
        ['clean'],
      ],
      handlers: { image: imageHandler },
    },
  }), [token]);

  return (
    <ReactQuill
      ref={quillRef}
      theme="snow"
      value={value}
      onChange={onChange}
      modules={modules}
      placeholder="팝업 본문 작성 (이미지 전용 모드면 표시되지 않음)"
    />
  );
}

function PreviewOverlay({ form, onClose }) {
  const isHtml = (form.content || '').includes('<');
  const boxStyle = { maxWidth: (parseInt(form.width, 10) || 400) + 'px' };
  const imageOnly = form.image_only && form.image_url;
  const position = form.position || 'top-left';
  const showLogo = form.show_logo !== false;

  return (
    <div className={`medi-popup-overlay pos-${position}`} onClick={(e) => { if (e.target.classList.contains('medi-popup-overlay')) onClose(); }}>
      <div className="medi-popup-box" style={boxStyle}>
        <div style={{
          background: '#fff3cd',
          color: '#856404',
          padding: '6px 12px',
          fontSize: 12,
          fontWeight: 600,
          textAlign: 'center',
          borderBottom: '1px solid #ffe69c',
        }}>
          미리보기 — 실제 저장은 아직 안 됨
        </div>
        {showLogo && (
          <div className="medi-popup-brand">
            <img src="/medilogis_logo.png" alt="MEDILOGIS" />
            <span className="medi-popup-brand-text">MEDI<b>LOGIS</b></span>
          </div>
        )}
        {imageOnly ? (
          <div className="medi-popup-image medi-popup-image-only">
            <img src={form.image_url} alt={form.title || ''} />
            {form.link_url && (
              <div style={{ padding: '8px 16px', fontSize: 12, color: '#888', background: '#fafafa' }}>
                ↗ 클릭 시 이동: {form.link_url}
              </div>
            )}
          </div>
        ) : (
          <>
            <div className="medi-popup-header">
              <h3 className="medi-popup-title">{form.title || '(제목 없음)'}</h3>
            </div>
            {form.image_url && (
              <div className="medi-popup-image">
                <img src={form.image_url} alt={form.title} />
              </div>
            )}
            <div className="medi-popup-body">
              {form.content ? (
                isHtml ? (
                  <div dangerouslySetInnerHTML={{ __html: form.content }} />
                ) : (
                  <p style={{whiteSpace:'pre-wrap'}}>{form.content}</p>
                )
              ) : (
                <p style={{ color: '#999' }}>(내용 없음)</p>
              )}
              {form.link_url && (
                <p style={{ marginTop: 12, fontSize: 12, color: '#888' }}>
                  ↗ 클릭 시 이동: {form.link_url}
                </p>
              )}
            </div>
          </>
        )}
        <div className="medi-popup-footer">
          <label className="medi-popup-hide">
            <input type="checkbox" disabled /> 오늘 하루 보지 않기 (미리보기)
          </label>
          <button type="button" className="medi-popup-close" onClick={onClose}>닫기</button>
        </div>
      </div>
    </div>
  );
}

function toDateTimeLocal(s) {
  // ISO -> 'yyyy-MM-ddTHH:mm' 형태
  const d = new Date(s);
  const pad = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default PopupAdmin;
